import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import BookingWidget from "@/components/BookingWidget";
import { supabase } from "@/integrations/supabase/client";
import { useSeo } from "@/hooks/useSeo";
import { Skeleton } from "@/components/ui/skeleton";
import heroFallback from "@/assets/hero-transfers.jpg";

interface LandingData {
  title: string;
  content: string;
  hero_image_url: string | null;
  meta_title: string | null;
  meta_description: string | null;
  meta_keywords: string | null;
}

const LandingPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState<LandingData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const { data: page } = await supabase
        .from("landing_pages")
        .select("title, content, hero_image_url, meta_title, meta_description, meta_keywords")
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();
      setData(page);
      setLoading(false);
    })();
  }, [slug]);

  const fallbackTitle = slug?.replace(/-/g, " ") || "Transfer";

  useSeo({
    title: data?.meta_title || data?.title || `${fallbackTitle} | Allen Travels RD`,
    description: data?.meta_description || `Book your ${fallbackTitle} with Allen Travels RD — premium transfers and tours in the Dominican Republic.`,
    keywords: data?.meta_keywords || undefined,
  });

  return (
    <div className="min-h-screen bg-gradient-soft flex flex-col">
      <Navbar />

      <PageHero
        eyebrow="Allen Travels RD"
        title={
          loading ? (
            <Skeleton className="h-12 w-3/4 bg-white/20" />
          ) : (
            <span className="capitalize">{data?.title || fallbackTitle}</span>
          )
        }
        subtitle="Premium transfers across the Dominican Republic — book in seconds, ride in comfort."
        image={data?.hero_image_url || heroFallback}
        size="md"
      />

      <main className="flex-1 -mt-16 relative z-20 pb-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <BookingWidget />
          </motion.div>

          {data?.content && (
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-16 max-w-3xl mx-auto glass shadow-card rounded-2xl p-8 md:p-10"
            >
              <div
                className="prose prose-lg max-w-none prose-headings:font-display prose-headings:text-foreground prose-a:text-primary"
                dangerouslySetInnerHTML={{ __html: data.content }}
              />
            </motion.article>
          )}

          {!loading && !data && (
            <div className="mt-16 text-center text-muted-foreground">
              <p>This landing page is being prepared. Please use the booking widget above.</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LandingPage;
